<template>
  <CDropdown
    inNav
    class="c-header-nav-items user-account-dropdown"
    placement="bottom-end"
    add-menu-classes="p-0 shadow-lg border-0 user-profile-dropdown-menu"
  >
    <template #toggler>
      <CHeaderNavLink class="p-0 avatar-nav-link" :title="displayName">
        <div class="c-avatar user-avatar-badge">
          <img :src="userAvatar" class="c-avatar-img" alt="User Avatar" />
        </div>
      </CHeaderNavLink>
    </template>

    <div class="user-profile-card">
      <!-- Profile Card Header with Gradient -->
      <div class="user-profile-header">
        <div class="user-profile-avatar-wrap">
          <img :src="userAvatar" class="user-profile-avatar-img" alt="Avatar" />
        </div>
        <div class="user-profile-info">
          <div class="user-profile-name" :title="displayName">
            {{ displayName }}
          </div>
          <div class="user-profile-email text-truncate" :title="userEmail">
            {{ userEmail }}
          </div>
          <div class="user-profile-badges mt-2">
            <span class="badge-role">{{ roleDisplayName }}</span>
            <span v-if="affiliationName" class="badge-affiliation text-truncate" :title="affiliationName">
              {{ affiliationName }}
            </span>
          </div>
        </div>
      </div>

      <div class="dropdown-divider m-0"></div>

      <!-- Actions -->
      <div class="user-profile-actions p-2">
        <CDropdownItem @click="goToProfile" class="user-profile-item rounded mb-1">
          <CIcon name="cil-user" size="lg" class="mr-2 text-primary" />
          <span class="font-weight-bold text-dark">{{ $t('profile.title') }}</span>
        </CDropdownItem>
        <CDropdownItem @click="logout" class="user-logout-btn rounded">
          <CIcon name="cil-account-logout" size="lg" class="mr-2 text-danger" />
          <span class="text-danger font-weight-bold">{{ $t('common.logout') }}</span>
        </CDropdownItem>
      </div>
    </div>
  </CDropdown>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getVerifiedBlendataAffiliationName } from "@/utils/affiliation";

export default {
  name: "TheHeaderDropdownAccnt",
  computed: {
    ...mapGetters("auth", ["profile", "role"]),
    displayName() {
      if (!this.profile) return "User";
      const full = `${this.profile.name || ""} ${this.profile.surname || ""}`.trim();
      return full || this.profile.username || "User";
    },
    userEmail() {
      return this.profile?.email || "-";
    },
    userAvatar() {
      const a = this.profile?.avatar;
      if (!a) return require("@/assets/avatars/8.jpg");
      if (typeof a === "string" && a.startsWith("preset:")) {
        const num = a.replace("preset:", "");
        try {
          return require(`@/assets/avatars/${num}.jpg`);
        } catch (e) {
          return require("@/assets/avatars/8.jpg");
        }
      }
      return a;
    },
    roleDisplayName() {
      const isEn = this.$store?.state?.locale?.lang === "en";
      const r = (this.profile?.role || this.role || "user").toLowerCase();
      const mapTh = {
        user: "ผู้ใช้งานทั่วไป",
        technician: "ช่างเทคนิค",
        supervisor: "หัวหน้างาน",
        admin: "ผู้ดูแลระบบ",
        student_affairs: "ส่วนพัฒนานักศึกษา",
      };
      const mapEn = {
        user: "User",
        technician: "Technician",
        supervisor: "Supervisor",
        admin: "Admin",
        student_affairs: "Student Affairs",
      };
      return (isEn ? mapEn[r] : mapTh[r]) || (isEn ? "User" : "ผู้ใช้งานทั่วไป");
    },
    affiliationName() {
      if (!this.profile) return "";
      const verifiedAff = getVerifiedBlendataAffiliationName(this.profile.affiliation);
      if (verifiedAff) {
        return verifiedAff;
      }
      const sec = this.profile.section;
      if (sec) {
        return typeof sec === "object" ? (sec.departmentName || sec.name || "") : String(sec);
      }
      const dept = this.profile.department;
      if (dept) {
        return typeof dept === "object" ? (dept.name || "") : String(dept);
      }
      return "";
    },
  },
  methods: {
    ...mapActions("auth", ["logout"]),
    goToProfile() {
      const path = (this.profile?.role || this.role) === 'student_affairs' ? '/student-affairs/profile' : '/supervisor/profile';
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
  },
};
</script>

<style scoped>
.user-profile-dropdown-menu {
  min-width: 290px;
  max-width: 330px;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 12px 35px rgba(37, 99, 235, 0.15) !important;
  border: 1px solid rgba(37, 99, 235, 0.08) !important;
}

.user-profile-card {
  background: #ffffff;
}

.user-profile-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #1e40af;
  color: #ffffff;
}

.user-profile-avatar-wrap {
  position: relative;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.85);
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
}

.user-profile-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-profile-info {
  min-width: 0;
  flex: 1 1 auto;
}

.user-profile-name {
  font-size: 15px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-profile-email {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
  line-height: 1.2;
}

.user-profile-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
}

.badge-role {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.badge-affiliation {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.95);
  max-width: 130px;
}

.user-logout-btn {
  padding: 10px 14px;
  font-size: 13px;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-logout-btn:hover {
  background-color: #fff1f0 !important;
  color: #c92a2a !important;
}

.avatar-nav-link {
  display: flex;
  align-items: center;
}

.user-avatar-badge {
  border: 2px solid #1e40af;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.user-avatar-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 10px rgba(37, 99, 235, 0.25);
}
</style>
